import { Badge, cn, type BadgeProps } from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import type { ClubRelationship } from '../lib/clubsSearch';

/**
 * Relationship warmth, shown as a badge and — where space is tight — as a dot.
 *
 * Both read from one map so the two never drift: a dot that says warm next to a
 * badge that says cold is worse than showing neither.
 */
const BADGE_VARIANTS: Record<ClubRelationship, BadgeProps['variant']> = {
  strong: 'success',
  active: 'warning',
  cold: 'secondary',
};

const DOT_CLASSES: Record<ClubRelationship, string> = {
  strong: 'bg-success',
  active: 'bg-warning',
  cold: 'bg-muted-foreground/50',
};

export function RelationshipTag({ value }: { value: ClubRelationship }) {
  const { t } = useTranslation();

  return (
    <Badge variant={BADGE_VARIANTS[value]} className="uppercase tracking-wide">
      {t(`clubs.relationship.${value}`)}
    </Badge>
  );
}

/** The same information as a small dot beside the club name. */
export function RelationshipDot({ value }: { value: ClubRelationship }) {
  const { t } = useTranslation();

  return (
    <span
      role="img"
      aria-label={t(`clubs.relationship.${value}`)}
      title={t(`clubs.relationship.${value}`)}
      className={cn('inline-block size-[7px] flex-none rounded-full', DOT_CLASSES[value])}
    />
  );
}
